/** Production endpoints (`/production`): machines + conversion runs. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, patchData, postData } from "../lib/apiClient";
import { apiClient } from "../lib/apiClient";
import { ApiEnvelope } from "../types/api";
import { queryKeys } from "./queryKeys";
import { Machine, ProductionRun, ProductionStatus, Shift } from "../types/models";

export interface ListRunsParams {
  page?: number;
  limit?: number;
  status?: ProductionStatus;
  shift?: Shift;
  machineId?: string;
  supervisorId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface RunLineInput {
  productId: string;
  quantity: number;
}

export interface CreateRunInput {
  machineId?: string;
  supervisorId?: string;
  shift?: Shift;
  notes?: string;
  inputs?: RunLineInput[];
  outputs?: RunLineInput[];
}

export interface UpdateRunInput {
  machineId?: string | null;
  supervisorId?: string | null;
  shift?: Shift;
  notes?: string;
}

export interface CreateMachineInput {
  name: string;
  code: string;
}

export interface UpdateMachineInput {
  name?: string;
  code?: string;
  isActive?: boolean;
}

export const productionApi = {
  listMachines: () =>
    apiClient
      .get<ApiEnvelope<Machine[]>>("/production/machines")
      .then((r) => r.data.data),
  createMachine: (input: CreateMachineInput) =>
    postData<Machine>("/production/machines", input),
  updateMachine: (id: string, input: UpdateMachineInput) =>
    patchData<Machine>(`/production/machines/${id}`, input),

  listRuns: (params?: ListRunsParams) =>
    getList<ProductionRun>("/production/runs", params),
  getRun: (id: string) => getData<ProductionRun>(`/production/runs/${id}`),
  createRun: (input: CreateRunInput) =>
    postData<ProductionRun>("/production/runs", input),
  updateRun: (id: string, input: UpdateRunInput) =>
    patchData<ProductionRun>(`/production/runs/${id}`, input),
  addInput: (id: string, input: RunLineInput) =>
    postData<ProductionRun>(`/production/runs/${id}/inputs`, input),
  addOutput: (id: string, input: RunLineInput) =>
    postData<ProductionRun>(`/production/runs/${id}/outputs`, input),
  completeRun: (id: string) =>
    postData<ProductionRun>(`/production/runs/${id}/complete`),
  cancelRun: (id: string) =>
    postData<ProductionRun>(`/production/runs/${id}/cancel`),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useMachines = () =>
  useQuery({
    queryKey: queryKeys.production.machines,
    queryFn: () => productionApi.listMachines(),
  });

export const useProductionRuns = (params?: ListRunsParams) =>
  useQuery({
    queryKey: queryKeys.production.runs(params),
    queryFn: () => productionApi.listRuns(params),
    placeholderData: (prev) => prev,
  });

export const useProductionRun = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.production.run(id ?? ""),
    queryFn: () => productionApi.getRun(id as string),
    enabled: Boolean(id),
  });

const invalidateProduction = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: queryKeys.production.all });
  qc.invalidateQueries({ queryKey: queryKeys.inventory.all });
  qc.invalidateQueries({ queryKey: queryKeys.dashboard.overview });
};

export const useCreateRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRunInput) => productionApi.createRun(input),
    onSuccess: () => invalidateProduction(qc),
  });
};

export const useUpdateRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRunInput }) =>
      productionApi.updateRun(id, input),
    onSuccess: () => invalidateProduction(qc),
  });
};

export const useAddRunInput = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RunLineInput }) =>
      productionApi.addInput(id, input),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.production.run(id) });
      qc.invalidateQueries({ queryKey: queryKeys.production.all });
    },
  });
};

export const useAddRunOutput = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RunLineInput }) =>
      productionApi.addOutput(id, input),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.production.run(id) });
      qc.invalidateQueries({ queryKey: queryKeys.production.all });
    },
  });
};

export const useCompleteRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productionApi.completeRun(id),
    onSuccess: () => invalidateProduction(qc),
  });
};

export const useCancelRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productionApi.cancelRun(id),
    onSuccess: () => invalidateProduction(qc),
  });
};

export const useCreateMachine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMachineInput) => productionApi.createMachine(input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.production.machines }),
  });
};

export const useUpdateMachine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMachineInput }) =>
      productionApi.updateMachine(id, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.production.machines }),
  });
};
